import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Button,
  Skeleton,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "@context/NotificationContext";
import { extractErrorMessage } from "@api/axios";
import * as eventsApi from "@api/events.api";
import * as reviewsApi from "@api/reviews.api";
import type { Event, Review } from "@/types";
import { ConfirmDialog } from "@components/ConfirmDialog";

export default function EventDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { notify } = useNotification();

  const [event, setEvent] = useState<Event | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      eventsApi.getById(id),
      reviewsApi.getAll({ eventId: id }),
    ]).then(([ev, revs]) => {
      setEvent(ev);
      setReviews(revs);
      setLoading(false);
    });
  }, [id]);

  const hasReviewed = reviews.some((r) => r.userId === user?.id);

  const handleDelete = async () => {
    if (!id) return;
    try {
      await eventsApi.remove(id);
      notify(t("events.deleted"), "success");
      navigate("/dashboard");
    } catch (err) {
      notify(extractErrorMessage(err, t("common.unexpectedError")), "error");
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Skeleton variant="rounded" height={200} />
      </Box>
    );
  }

  if (!event) return null;

  const reviewedUserIds = new Set(reviews.map((r) => r.userId));

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 700 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            flex: 1,
          }}
        >
          {t("events.details")}
        </Typography>
        <IconButton color="error" onClick={() => setDeleteOpen(true)}>
          <DeleteIcon />
        </IconButton>
      </Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 700,
            }}
          >
            {event.name}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <CalendarTodayIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              {new Date(event.startedAt).toLocaleString()}
              {event.endedAt
                ? ` - ${new Date(event.endedAt).toLocaleString()}`
                : ""}
            </Typography>
          </Box>
          {event.restaurant && (
            <>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <RestaurantIcon
                  sx={{ fontSize: 18, color: "text.secondary" }}
                />
                <Typography variant="body2">{event.restaurant.name}</Typography>
              </Box>
              {event.restaurant.location && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LocationOnIcon
                    sx={{ fontSize: 18, color: "text.secondary" }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    {event.restaurant.location}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
      {!hasReviewed && event.restaurant && (
        <Button
          variant="contained"
          fullWidth
          sx={{ mb: 3 }}
          onClick={() => navigate(`/events/${id}/review`)}
        >
          {t("events.submitReview")}
        </Button>
      )}
      <Card>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("events.participants")} ({event.eventUsers?.length || 0})
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <List disablePadding>
            {event.eventUsers?.map((eu) => (
              <ListItem key={eu.userId} disablePadding sx={{ py: 0.5 }}>
                <ListItemAvatar>
                  <Avatar sx={{ width: 36, height: 36, fontSize: 14 }}>
                    {eu.user?.email?.[0]?.toUpperCase() || "?"}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={eu.user?.email || eu.userId}
                  slotProps={{ primary: { sx: { fontSize: 14 } } }}
                />
                {reviewedUserIds.has(eu.userId) ? (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={t("events.submitted")}
                    size="small"
                    color="success"
                  />
                ) : (
                  <Chip
                    icon={<PendingIcon />}
                    label={t("events.pending")}
                    size="small"
                    color="warning"
                  />
                )}
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
      <ConfirmDialog
        open={deleteOpen}
        title={t("common.delete")}
        message={t("events.deleteConfirm")}
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Box>
  );
}
